import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Chip, DataTable, EmptyState, FilterRow, Icon, Segmented, StatCard,
  type TableRow,
} from '@/components/kit';
import { useShellChrome, useNarrowViewport } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useAuthStore } from '@/stores/useAuthStore';
import { ManagerSurface } from '../components/ManagerSurface';
import { ManagerMenu } from '../components/ManagerMenu';
import {
  dayScopeLabel, dayScopeRange, doctorName, money, num, todayYmd, ymd,
  type DayScope,
} from '../lib/scope';
import {
  splitByBranch, useAppointments, useBranchesState, useInvoices, useProviders, useUnreviewedResults,
  useUnsignedNotes,
} from '../lib/useManagerData';

/**
 * Clinic manager · Today — `maie-desktop-clinic-manager/g_today.dc.html`.
 *
 * THE HOME IS AGGREGATES ONLY, and the last line of the board is the rule the
 * whole screen is built to keep: *a patient name never appears on a manager's
 * home*. So nothing here reads a patient relation, and the closest it comes to
 * a person is the doctor a count belongs to.
 *
 * THREE LIVE SCOPES. Today / this week / this month re-read every figure — the
 * canvas's pill is not a label on a fixed day.
 *
 * WHAT IS NOT HERE, and why. The board's branch table carries "Maadi" and
 * "Nasr City"; there is no branch model in the backend (see lib/scope.ts), so
 * the dimension has one row — the clinic. It is a real row read from the
 * session, not two invented ones.
 */
/**
 * WHAT A QUIET DAY IS ALLOWED TO CLAIM. `g_today_clean`'s empty state reads
 * "Both days closed, every note signed, nothing unpaid" — but whether a branch
 * has closed its day is not a read this home makes (day close lives on the
 * desk), so the sentence names only the facts this screen is actually holding
 * and never asserts the third. Unpaid money is outside the amber card's own
 * test, so a day with an open balance says so rather than printing a clean
 * sentence over it.
 */
function cleanDayLine(unpaid: number, resultsRecorded: boolean): string {
  const facts = ['every note signed'];
  if (resultsRecorded) facts.push('no result waiting to be read');
  if (unpaid === 0) facts.push('nothing unpaid');
  const last = facts.pop() as string;
  const head = facts.length ? `${facts.join(', ')} and ${last}` : last;
  return `${head[0].toUpperCase()}${head.slice(1)}. Anything that needed you would be an amber card in this spot.`;
}

export function ManagerTodayPage() {
  const narrow = useNarrowViewport();
  const go = useNavigate();
  const [scope, setScope] = useState<DayScope>('today');
  const [query, setQuery] = useState('');

  const range = useMemo(() => dayScopeRange(scope), [scope]);
  const invoices = useInvoices(range);
  const appointments = useAppointments(range);
  const { providers, isLoading: providersLoading } = useProviders();
  const unsigned = useUnsignedNotes();
  const { results, isLoading: resultsLoading } = useUnreviewedResults();
  const { branches, isLoading: branchesLoading } = useBranchesState();
  /* Until every read has answered, a figure is not 0 — it is not known yet. */
  const loading = invoices.isLoading || appointments.isLoading || providersLoading || branchesLoading
    || unsigned.isLoading || resultsLoading;

  usePageMeta({ title: 'Today' });

  /* Tomorrow's booked list is its own read — it is outside every scope the
     pill offers, and the board prints it whichever scope is on. */
  const tomorrow = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return ymd(d);
  }, []);
  const tomorrowBookings = useAppointments({ start: tomorrow, end: tomorrow });

  /* A visit is an appointment that happened or is happening. Cancelled and
     no-show rows are in the same list and are NOT visits — counting them is
     how a clinic's day reads busier than it was. */
  const visits = useMemo(
    () => appointments.page.rows.filter((a) => a.status !== 'cancelled' && a.status !== 'no-show'),
    [appointments.page.rows],
  );
  const followUps = visits.filter((a) => a.appointmentType === 'follow-up').length;
  const fresh = visits.length - followUps;

  const collected = invoices.page.rows.reduce((s, i) => s + Number(i.amountPaid || 0), 0);
  const unpaid = invoices.page.rows.reduce((s, i) => s + Number(i.balance || 0), 0);

  /* Unsigned notes, per doctor. The manager gets the COUNT — never the note,
     which is the wall the board's closing line names. */
  const unsignedByProvider = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of unsigned.page.rows) {
      if (!r.providerId) continue;
      m.set(r.providerId, (m.get(r.providerId) ?? 0) + 1);
    }
    return m;
  }, [unsigned.page.rows]);

  /* Seen, per doctor, inside the scope. */
  const seenByProvider = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of visits) {
      if (!a.providerId) continue;
      m.set(a.providerId, (m.get(a.providerId) ?? 0) + 1);
    }
    return m;
  }, [visits]);

  const doctors = useMemo(
    () =>
      providers
        .map((p) => ({
          id: p.id,
          name: doctorName(p),
          seen: seenByProvider.get(p.id) ?? 0,
          unsigned: unsignedByProvider.get(p.id) ?? 0,
        }))
        .filter((d) => d.seen > 0 || d.unsigned > 0)
        .sort((a, b) => b.seen - a.seen),
    [providers, seenByProvider, unsignedByProvider],
  );

  const branchName = branches[0]?.name ?? 'This clinic';
  /* Visits, notes and bookings are not stamped with a site, so beside two sites they
     are the CLINIC's — labelled that way rather than with the first branch's name. */
  const clinicName = useAuthStore((st) => st.activeTenant?.displayName?.trim()) || branchName;
  const scopeName = branches.length > 1 ? clinicName : branchName;
  const totalUnsigned = unsigned.page.total;

  /* THE BANNER IS TWO CLAUSES ON THE BOARD — "2 unsigned notes (Nasr City) ·
     1 result unreviewed 3 d" — and a clause is never dropped in silence. The
     unsigned count is real; the results backlog is real (the lab and imaging
     stats endpoints both carry an `unreviewed` count); the "3 d" is not
     recorded anywhere, so it is left off rather than guessed, and when neither
     stats endpoint answers the clause says so instead of reading zero. */
  const attention: string[] = [];
  if (totalUnsigned > 0) {
    attention.push(`${totalUnsigned} unsigned note${totalUnsigned === 1 ? '' : 's'} (${scopeName})`);
  }
  if (results.recorded && results.unreviewed > 0) {
    attention.push(`${results.unreviewed} result${results.unreviewed === 1 ? '' : 's'} unreviewed`);
  }
  const showAttention = totalUnsigned > 0 || results.unreviewed > 0;

  const shownDoctors = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return doctors;
    return doctors.filter((d) => d.name.toLowerCase().includes(q));
  }, [doctors, query]);

  /* THE BRANCH TABLE — `g_c1`'s branch cards. Money splits by the site each
     bill was raised at; visits and doctors are not stamped with a site, so
     with more than one branch a row counts its BILLS instead of claiming a
     visit split nobody recorded. Opening a row is the ledger for the scope. */
  const split = useMemo(() => splitByBranch(invoices.page.rows, branches), [invoices.page.rows, branches]);
  const branchSig = split.map((b) => `${b.id}:${b.collected}:${b.bills}`).join('|');
  const branchRows: TableRow[] = useMemo(() => {
    if (!visits.length && collected === 0 && unpaid === 0) return [];
    if (split.length > 1) {
      const q = query.trim().toLowerCase();
      return split
        .filter((b) => !q || b.name.toLowerCase().includes(q))
        .map((b) => ({
          id: b.id ?? 'clinic',
          cells: {
            branch: (
              <div>
                <p className="t-strong">{b.name}</p>
                <p className="t-cap mut">open the ledger ›</p>
              </div>
            ),
            visits: (
              <span className="mut">
                {b.bills} bill{b.bills === 1 ? '' : 's'}{b.unpaid ? ` · ${b.unpaid} unpaid` : ''}
              </span>
            ),
            revenue: <span className="w6 num">{num(b.collected)}</span>,
            doctors: <span className="mut">—</span>,
          },
        }));
    }
    /* The search narrows what is on screen, and the branch is on screen — a
       box that says "Search branches, doctors, staff" and only ever filters
       the doctor cards is half a dead control. */
    const q = query.trim().toLowerCase();
    if (q && !branchName.toLowerCase().includes(q) && !shownDoctors.length) return [];
    const docs = doctors;
    return [{
      id: 'branch-0',
      cells: {
        branch: (
          <div>
            <p className="t-strong">{branchName}</p>
            <p className="t-cap mut">open the ledger ›</p>
          </div>
        ),
        visits: (
          <span className="mut">
            {visits.length} · {fresh} new / {followUps} f-u
          </span>
        ),
        revenue: <span className="w6 num">{num(collected)}</span>,
        /* ONE LINE, like the board's. Two doctors fit the 24% column at desk
           width; a third makes the row three lines tall and the table stops
           being scannable, so the rest are counted rather than listed. */
        doctors: docs.length ? (
          <span className="mut" style={{ display: 'block', whiteSpace: 'nowrap',
            overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {docs.slice(0, 2).map((d, i) => (
              <span key={d.id}>
                {i > 0 ? ' · ' : null}
                {d.name}{' '}
                <span className={`${d.unsigned ? 'amb' : 'grn'} t-cap w6`}>
                  {d.unsigned} unsigned{d.unsigned ? '' : ' ✓'}
                </span>
              </span>
            ))}
            {docs.length > 2 ? ` · +${docs.length - 2}` : null}
          </span>
        ) : (
          <span className="mut">nobody worked this scope</span>
        ),
      },
    }];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchName, visits.length, fresh, followUps, collected, unpaid, doctors, query, shownDoctors.length, branchSig]);

  useShellChrome(
    {
      raw: true,
      phoneMenu: (close) => <ManagerMenu door="today" onClose={close} />,
      /* Phone `g_c1`: the date is the title, "All branches" its caption, and
         the strip ends in the alerts bell (with its count) and the reports
         door; there is no search box on the board. */
      title: narrow
        ? new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' })
        : 'Today',
      sub: narrow ? 'All branches' : <>{dayScopeLabel(scope)} · all branches</>,
      search: 'Search branches, doctors, staff, the log…',
      searchValue: query,
      onSearchChange: setQuery,
      phoneSearch: false,
      phoneTrailing: (
        <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Chip kind="soft" className="chip-act w6" label="Alerts" onClick={() => go('/clinic-manager/alerts')}>
            <Icon name="bell" size="sm" />{attention.length ? ` ${attention.length}` : ''}
          </Chip>
          <button type="button" className="ts-btn" aria-label="Reports" onClick={() => go('/clinic-manager/reports')}>
            <Icon name="chart-bar" size="lg" />
          </button>
        </span>
      ),
    },
    [scope, query, branchName, narrow, attention.length],
  );

  return (
    <div className="maie desk">
      <ManagerSurface />
      <div className="dcontent">
        <FilterRow>
          <Segmented
            pill="brand"
            label="Period"
            active={scope}
            onSelect={(k) => setScope(k as DayScope)}
            items={[
              { key: 'today', label: 'Today' },
              { key: 'week', label: 'This week' },
              { key: 'month', label: 'This month' },
            ]}
          />
          {narrow ? null : <>
            <span className="t-cap mut" style={{ padding: '0 4px' }}>·</span>
            <span className="t-sec mut">all branches</span>
          </>}
        </FilterRow>

        {narrow ? null : <div className="kit-stats" style={{ display: 'flex', gap: 12, margin: '4px 0 20px' }}>
          <StatCard label={scope === 'today' ? 'Collected today' : 'Collected'} value={loading ? '…' : money(collected)} tone="success" />
          <StatCard label="Visits" value={loading ? '…' : num(visits.length)} />
          {/* AMBER IS A CLAIM, NOT A LABEL. `g_today_clean` prints the same tile
              green at zero — "Unpaid at close · 0" is the good news, and a
              permanently amber figure teaches the manager to stop reading it. */}
          <StatCard label="Unpaid at close" value={loading ? '…' : num(unpaid)} tone={loading ? undefined : unpaid > 0 ? 'warn' : 'success'} />
        </div>}

        {narrow ? (
          <div style={{ display: 'grid', gap: 10, margin: '12px 0 16px' }}>
            {(split.length > 1 ? split : [{ id: 'branch-0', name: branchName, collected, bills: 0, unpaid }])
              .filter((b) => !query.trim() || b.name.toLowerCase().includes(query.trim().toLowerCase()))
              .map((b, i) => (
                <button key={b.id ?? i} type="button" className="card card-btn"
                  style={{ textAlign: 'left', width: '100%', padding: 14, boxSizing: 'border-box', margin: 0 }}
                  onClick={() => go('/clinic-manager/today/ledger')}>
                  <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span className="t-strong">{b.name}</span>
                    <span className="w6 num">{loading ? '…' : `EGP ${num(b.collected)}`}</span>
                  </span>
                  <span className="t-sec mut" style={{ display: 'block', marginTop: 4 }}>
                    {split.length > 1
                      ? `${b.bills} bill${b.bills === 1 ? '' : 's'}${b.unpaid ? ` · ${b.unpaid} unpaid` : ''}`
                      : loading ? 'counting the day…' : <>
                          {visits.length} visits · <span className="w6">{fresh} new</span> · <span className="w6">{followUps} follow-up</span>
                          {doctors.length ? ` · ${doctors.slice(0, 2).map((d) => d.name).join(', ')}` : ''}
                          {unpaid ? ` · ${num(unpaid)} unpaid` : ''}
                        </>}
                  </span>
                </button>
              ))}
          </div>
        ) : null}

        {narrow ? null : <DataTable
          caption="Branches"
          /* THE DECLARED WIDTHS ARE THE BOARD'S, AND `table-layout: auto`
             ignores them: it redistributes 46/18/12/24 around the widest cell,
             which put VISITS 95px, REVENUE 123px and DOCTORS 131px left of the
             artboard's. One class — the same one the ledger carries
             (manager.css §5) — makes the table lay out where it is told. */
          className="cm-fixed-cols"
          columns={[
            { key: 'branch', label: 'Branch', width: 46 },
            { key: 'visits', label: 'Visits', width: 18 },
            /* On a phone the money is the chip, not the doctors: the last
               column is a nowrap list of names, which is prose rather than a
               state and would be the one thing a card truncated. */
            { key: 'revenue', label: 'Revenue', width: 12, role: 'trailing' },
            { key: 'doctors', label: 'Doctors', width: 24 },
          ]}
          rows={branchRows}
          onSelect={() => go('/clinic-manager/today/ledger')}
          empty={loading ? <p className="t-sec mut">Counting the day…</p> :
            <EmptyState
              title="Nothing has happened yet"
              sub="The first visit of the scope puts your branch here, with its visits, its money and the doctors who worked it."
            />}
        />}

        <p className="t-ovl mut" style={{ margin: '20px 0 8px' }}>Doctors</p>
        {loading ? (
          <p className="t-sec mut" style={{ marginBottom: 16 }}>Counting the day…</p>
        ) : shownDoctors.length ? (
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            {shownDoctors.slice(0, 3).map((d) => (
              <Card key={d.id} style={{ flex: 1, minWidth: 220, margin: 0,
                borderColor: d.unsigned ? 'var(--warning-600)' : undefined }}
                onClick={() => go(`/clinic-manager/reports/visits?doctor=${d.id}`)}>
                <p className="t-strong">
                  {d.name} <Chip kind="soft" small>{scopeName}</Chip>
                </p>
                {/* THREE CLAUSES, as the board draws every one of these cards
                    ("14 seen · 0 unsigned · results same-day ✓"). The board's
                    third clause is a contract or a turnaround fact and neither
                    is modelled, so the clause that is always true of this card
                    — it opens the doctor's own report — takes the slot, rather
                    than the card dropping to two. */}
                <p className={`t-sec ${d.unsigned ? 'amb' : 'grn'} mt4`}>
                  {d.seen} seen · {d.unsigned} unsigned{d.unsigned ? '' : ' ✓'} · open the card →
                </p>
              </Card>
            ))}
          </div>
        ) : (
          <Card kind="ghost" style={{ marginBottom: 16 }}>
            <p className="t-strong">No doctor has worked this scope</p>
            <p className="t-sec mut mt8">
              A doctor appears here with what they saw and what they still owe the moment their
              first visit of the period is booked.
            </p>
          </Card>
        )}

        {/* THE AMBER CARD IS ABSENT ON A CLEAN DAY — NOT EMPTY, ABSENT, which is
            `g_today_clean`'s own line and the whole point of the second board:
            an alert surface that draws a grey version of itself when there is
            nothing wrong is an alert surface nobody trusts. So the card is not
            greyed out, it is gone, and the day states its own state instead. */}
        {loading ? null : showAttention ? (
          <Card style={{ borderColor: 'var(--warning-600)', cursor: 'pointer' }}
            onClick={() => go('/clinic-manager/alerts')}>
            <p className="t-sec amb">Needs attention: {attention.join(' · ')}</p>
          </Card>
        ) : (
          <EmptyState
            title="Nothing needs you today"
            sub={cleanDayLine(unpaid, results.recorded)}
          />
        )}

        <div style={{ display: 'flex', flexDirection: narrow ? 'column' : 'row', gap: 12, alignItems: 'stretch', marginTop: 4 }}>
          <Card style={{ flex: 1, margin: 0 }}>
            <p className="t-ovl mut">Tomorrow</p>
            <p className="t-body mt4">
              {tomorrowBookings.page.total > 0
                ? `${scopeName} ${tomorrowBookings.page.total} booked`
                : `${scopeName} nothing booked yet`}
            </p>
          </Card>
          <Card
            style={{ flex: 1, margin: 0, borderColor: 'var(--beige-500)', background: 'var(--beige-100)',
              display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer' }}
            onClick={() => go('/clinic-manager/today/digest')}
          >
            <span className="amb"><Icon name="mail-opened" size="lg" /></span>
            <div className="grow">
              <p className="t-strong">Today’s digest</p>
              <p className="t-cap mut">
                {todayYmd() === range.end ? 'the day so far · yesterday’s is here now' : 'the period so far'}
              </p>
            </div>
            <Icon name="chevron-right" />
          </Card>
        </div>

        <p className="t-sec mut" style={{ marginTop: 12 }}>
          {showAttention
            ? 'Counts, money and doctors — the wall at width. A patient name never appears on a manager’s home.'
            : 'The manager’s home earns its keep on the days it is quiet.'}
        </p>
      </div>
    </div>
  );
}
